// Atribuição de aquisição — captura de PRIMEIRO TOQUE no cliente.
//
// ── POR QUE ISTO EXISTE (medido 29/07/2026) ────────────────────────────────
// O PostHog client-side enxerga 44% dos cadastros reais (525 de 1.199 em 30d
// contra auth.users). A perda não é bloqueador de anúncio: `oauth_started`
// alcança 827 pessoas, então o SDK funcionava para ~300 das que sumiram. A
// perda está no round-trip do OAuth dentro do navegador in-app:
//
//   clique de OAuth em webview (Instagram/FB) → 737, completou → 335 (45%)
//   clique de OAuth em navegador real         → 106, completou →  77 (73%)
//
// 87% dos cliques nascem em webview, porque o tráfego é de anúncio Meta.
// Custo por cadastro não pode ser dividido por um denominador que perde
// metade do numerador — então a atribuição é persistida no NOSSO banco, na
// transação do cadastro, e nunca depende de um beacon de analytics conseguir
// sair da página.
//
// ── DECISÕES DE DESENHO ────────────────────────────────────────────────────
//  1. PRIMEIRO toque vence. O blob é escrito uma vez e nunca sobrescrito:
//     quem chega por anúncio, sai, e volta direto continua creditando o
//     anúncio. O cadastro pode acontecer DIAS depois do toque — por isso
//     localStorage, e não sessionStorage (que não atravessa o intervalo).
//  2. Um toque SEM dado de atribuição NÃO é gravado. Gravar um blob vazio
//     numa visita direta bloquearia para sempre o toque pago posterior — é o
//     modo de falha que torna um modelo de primeiro-toque inútil na prática.
//  3. Valores têm teto de tamanho: eles viajam dentro de
//     auth.users.raw_user_meta_data, que está no caminho crítico da transação
//     de cadastro. Um utm_content gigante não pode ameaçar um signup.
//
// ── CONVIVÊNCIA DELIBERADA COM metaPixel.ts ────────────────────────────────
// `src/lib/metaPixel.ts` já guarda um fbclid em sessionStorage (`vyv_fbclid`).
// São coisas diferentes e NÃO devem ser unificadas: o do metaPixel é o fbclid
// CORRENTE, usado para reconstruir o cookie _fbc no match da CAPI; o daqui é o
// PRIMEIRO, que precisa sobreviver a semanas para creditar a campanha certa.

import { supabase } from "@/integrations/supabase/client";
import { getMetaFbc, getMetaFbp } from "./metaPixel";

/** Onde o primeiro toque fica até o cadastro acontecer. */
export const FIRST_TOUCH_KEY = "vyv_first_touch";

/** Marca de qual usuário já teve o primeiro toque gravado no servidor. */
const SYNCED_KEY = "vyv_first_touch_synced";

/**
 * Teto por valor. Generoso para nomes de campanha reais, curto o bastante
 * para que seis campos não inflem a metadata do signup.
 */
const MAX_VALUE_LEN = 200;

/**
 * Tetos dos campos que não são parâmetro de campanha. landing_url carrega a
 * querystring inteira do anúncio; fbc é fb.1.<ms>.<fbclid> (fbclid real chega
 * a ~150 chars); user_agent truncado em 400 preserva tudo que identifica
 * navegador/versão. Espelhados em attribution_clean() na migration — o
 * servidor é o contrato, estes são conveniência.
 */
const MAX_LANDING_URL_LEN = 500;
const MAX_FBC_LEN = 400;
const MAX_UA_LEN = 400;

/** Os únicos parâmetros capturados. Ordem = ordem de gravação. */
export const ATTRIBUTION_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
] as const;

export type AttributionParam = (typeof ATTRIBUTION_PARAMS)[number];

export type FirstTouch = Partial<Record<AttributionParam, string>> & {
  /** Momento do primeiro toque (epoch ms). */
  ts: number;
  /**
   * Página de ENTRADA do primeiro toque (href completo). Vira
   * event_source_url no evento de servidor da Conversions API. Carona, nunca
   * qualificador: só é gravada quando algum ATTRIBUTION_PARAM existe.
   */
  landing_url?: string;
};

/** Normaliza um valor cru de querystring: trim, teto, vazio vira ausente. */
function clean(raw: string | null): string | undefined {
  if (raw == null) return undefined;
  const trimmed = raw.trim().slice(0, MAX_VALUE_LEN);
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Extrai os parâmetros de atribuição de uma querystring.
 * Exportado para o teste conseguir exercitar o parsing sem tocar em `window`.
 */
export function parseAttribution(search: string): Partial<Record<AttributionParam, string>> {
  const params = new URLSearchParams(search);
  const out: Partial<Record<AttributionParam, string>> = {};
  for (const key of ATTRIBUTION_PARAMS) {
    const value = clean(params.get(key));
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/** Lê o primeiro toque já gravado, ou null. */
export function readFirstTouch(): FirstTouch | null {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const obj = parsed as Record<string, unknown>;
    const out: FirstTouch = { ts: typeof obj.ts === "number" ? obj.ts : 0 };
    for (const key of ATTRIBUTION_PARAMS) {
      const value = obj[key];
      if (typeof value === "string" && value.length > 0) {
        out[key] = value.slice(0, MAX_VALUE_LEN);
      }
    }
    if (typeof obj.landing_url === "string" && obj.landing_url.length > 0) {
      out.landing_url = obj.landing_url.slice(0, MAX_LANDING_URL_LEN);
    }
    // Um blob sem nenhum parâmetro não atribui nada — trate como ausente.
    return ATTRIBUTION_PARAMS.some((k) => out[k] !== undefined) ? out : null;
  } catch (e) {
    console.warn("[attribution] leitura do primeiro toque falhou:", e);
    return null;
  }
}

/**
 * Captura o primeiro toque a partir da URL atual.
 *
 * FIRST-WRITE-WINS: se já existe um primeiro toque gravado, esta função não
 * faz nada — é o que a torna "primeiro toque" e não "último toque".
 *
 * @param search querystring (default: a da página atual)
 * @param now    relógio injetável (o teste precisa de tempo determinístico)
 * @returns true se ESTA chamada gravou o primeiro toque
 */
export function captureFirstTouch(
  search?: string,
  now: () => number = Date.now,
): boolean {
  try {
    // Já gravado → nada a fazer. Checado ANTES de olhar a URL para que um
    // toque posterior com utm não sobrescreva o original.
    if (localStorage.getItem(FIRST_TOUCH_KEY)) return false;

    const found = parseAttribution(
      search ?? (typeof window !== "undefined" ? window.location.search : ""),
    );
    // Visita sem nenhum parâmetro: NÃO grava (decisão 2 no cabeçalho).
    if (Object.keys(found).length === 0) return false;

    const touch: FirstTouch = { ...found, ts: now() };
    // Página de entrada do toque — só existe quando o toque existe, então
    // nunca transforma visita direta em toque. Vira event_source_url do
    // evento de servidor.
    if (typeof window !== "undefined" && window.location?.href) {
      touch.landing_url = window.location.href.slice(0, MAX_LANDING_URL_LEN);
    }
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
    return true;
  } catch (e) {
    console.warn("[attribution] gravação do primeiro toque falhou:", e);
    return false;
  }
}

/**
 * Monta o payload que vai para o servidor (sem o `ts` interno, que vira
 * `first_touch_at` do lado de lá).
 */
export function toServerPayload(touch: FirstTouch): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const key of ATTRIBUTION_PARAMS) {
    if (touch[key] !== undefined) payload[key] = touch[key];
  }
  if (touch.landing_url) payload.landing_url = touch.landing_url;
  if (touch.ts > 0) payload.first_touch_at = new Date(touch.ts).toISOString();
  return payload;
}

/**
 * Sinais de match da Conversions API, lidos AGORA (não congelados no primeiro
 * toque): _fbp identifica o navegador e _fbc o clique — o cookie corrente é o
 * valor que o Meta espera. getMetaFbc() já reconstrói de sessionStorage
 * quando o redirect de OAuth comeu o cookie; quando o fbevents.js nunca
 * carregou (webview), nada disto existe e o SERVIDOR reconstrói fbc a partir
 * do fbclid + first_touch_at persistidos. user_agent acompanha fbc/fbp
 * porque o Meta pede client_user_agent junto dos browser ids.
 *
 * Exportada para o teste; injetável porque jsdom não deixa sobrescrever
 * navigator.userAgent.
 */
export function collectBrowserSignals(
  ua: string | undefined = typeof navigator !== "undefined" ? navigator.userAgent : undefined,
): Record<string, string> {
  const signals: Record<string, string> = {};
  try {
    const fbp = getMetaFbp();
    const fbc = getMetaFbc();
    if (fbp) signals.fbp = fbp.slice(0, MAX_VALUE_LEN);
    if (fbc) signals.fbc = fbc.slice(0, MAX_FBC_LEN);
    if (ua) signals.user_agent = ua.slice(0, MAX_UA_LEN);
  } catch (e) {
    console.warn("[attribution] coleta de sinais de navegador falhou:", e);
  }
  return signals;
}

/**
 * Metadata para `supabase.auth.signUp({ options: { data } })`.
 *
 * Este é o caminho ATÔMICO: o valor entra em `auth.users.raw_user_meta_data`
 * na própria transação de cadastro, e o trigger o copia para
 * `signup_attribution` sem depender de nenhuma chamada posterior do cliente.
 * Retorna undefined quando não há primeiro toque — aí o signUp segue igual.
 */
export function signupMetadata(): { vyv_attribution: Record<string, unknown> } | undefined {
  const touch = readFirstTouch();
  if (!touch) return undefined;
  return { vyv_attribution: { ...toServerPayload(touch), ...collectBrowserSignals() } };
}

/**
 * Caminho do OAuth: grava a atribuição do usuário JÁ autenticado.
 *
 * O OAuth não deixa injetar metadata na transação de cadastro (o
 * raw_user_meta_data vem do provedor), então este é o único ponto possível —
 * mas roda sobre a conexão Supabase que o app precisa para funcionar, e não
 * sobre um beacon de analytics, que é o que morre no navegador in-app.
 *
 * Idempotente dos dois lados: o servidor é first-write-wins e esta função
 * marca o usuário como sincronizado para não repetir a chamada a cada boot.
 * É seguro chamar em todo carregamento autenticado — é o que faz a auto-cura
 * de quem falhou no retorno do OAuth.
 */
export async function syncSignupAttribution(userId: string): Promise<void> {
  try {
    if (localStorage.getItem(SYNCED_KEY) === userId) return;

    const touch = readFirstTouch();
    if (!touch) return;

    // RPC ausente dos tipos gerados até a migration aplicar em prod; o cast
    // segue o padrão já usado em src/lib/referral.ts.
    const { error } = await (supabase as unknown as {
      rpc: (fn: string, args: Record<string, unknown>) => Promise<{ error: unknown }>;
    }).rpc("record_signup_attribution", {
      p: { ...toServerPayload(touch), ...collectBrowserSignals() },
    });

    if (error) {
      // Falha esperada com retry no próximo boot (a marca não é gravada), e
      // o fallback preserva o que o usuário veio fazer — warn, não error.
      // Ver CLAUDE.md §11, critério (b).
      console.warn("[attribution] record_signup_attribution falhou:", error);
      return;
    }
    localStorage.setItem(SYNCED_KEY, userId);
  } catch (e) {
    console.warn("[attribution] syncSignupAttribution lançou:", e);
  }
}

/** Só para teste — limpa o estado local. */
export function __clearAttributionForTest(): void {
  try {
    localStorage.removeItem(FIRST_TOUCH_KEY);
    localStorage.removeItem(SYNCED_KEY);
  } catch {
    /* noop */
  }
}
